<template>
  <Modal
    class="parametersModal"
    :isOpen="isOpen"
    :buttons="state.buttons"
    title="Paramètres de la campagne"
    @close="close"
    @callbackBtn1="close"
    @callbackBtn2="saveCampaignInformations()"
  >
    <template #extra>
      <TabsNavigation
        :tabs="tabsTitle"
        @changeTab="changeTab"
        :currentTab="currentTab"
      />
      <transition mode="out-in" name="fade">
        <InformationsTab
          v-if="state.currentTab === 0"
          :data="state.data"
          @changeInvitation="(value) => (state.data.enabled = value)"
          @changeName="(event) => (state.data.name = event.target.value)"
          @toggleError="(val) => (state.informationsError = val)"
        />
        <PreviewTab
          v-else-if="state.currentTab === 1"
          :data="state.data"
          @changeImage="(event) => (state.data.image_file = event)"
          @changePublicName="(event) => (state.data.public_name = event)"
          @changeJobTitle="(event) => (state.data.job_title = event)"
          @changeJobDescription="
            (event) => (state.data.job_description = event)
          "
          @toggleError="(val) => (state.cardError = val)"
        />
        <ActionsTab v-else :data="state.data" @archive="campaignArchived" />
      </transition>
    </template>
  </Modal>
</template>

<script setup>
import { reactive, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useCampaignStore } from "@/stores/campaign.js";
import { useImageStore } from "@/stores/image.js";
import { useUserStore } from "@/stores/user.js";
import InformationsTab from "./tabs/Informations.vue";
import PreviewTab from "./tabs/Preview.vue";
import ActionsTab from "./tabs/Actions.vue";
import { useAppStore } from "@/stores/app.js";

const emits = defineEmits(["close"]);

const toast = useToast();

const route = useRoute();

const router = useRouter();

const campaignStore = useCampaignStore();

const { uploadImage } = useImageStore();

const { hasPermission } = useUserStore();

const appStore = useAppStore();

const tabsTitle = computed(() => {
  const array = ["Informations de la campagne", "Aperçu côté talent"];

  if (hasPermission("members", 1)) {
    array.push("Actions sur la campagne");
  }

  return array;
});

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  campaign: {
    type: Object,
  },
  currentTab: {
    type: Number,
    default: 0,
  },
});

const state = reactive({
  currentTab: props.currentTab,
  data: {
    ...props.campaign,
    enabled: props.campaign.invitation_id ? true : false,
  },
  buttons: {
    column: false,
    data: [
      {
        label: "Annuler les modifications",
        class: "secondary",
      },
      {
        label: "Enregistrer les modifications",
        class: "green",
      },
    ],
  },
  cardError: false,
  informationsError: false,
});

async function saveCampaignInformations() {
  if (state.cardError) {
    return toast.error(
      "Les informations publiques de votre campagne ne sont pas valides, veillez à bien respecter la limite de caractères."
    );
  }

  if (state.informationsError) {
    return toast.error(
      "Le nom privé de votre campagne n'est pas valide, veuillez vérifier votre saisie"
    );
  }

  if (state.data.image_file) {
    const { image_id } = await uploadImage(
      props.campaign.company_id,
      "campaign_public_image",
      state.data.image_file
    );

    if (image_id) {
      state.data.image_id = image_id;
    } else {
      toast.error("Une erreur est survenue lors du téléchargement de l'image");
    }
  }

  if (state.data.enabled !== (props.campaign.invitation_id ? true : false)) {
    await campaignStore.updateCampaignInvitation(
      props.campaign.company_id,
      props.campaign.workspace_id,
      props.campaign.campaign_id,
      state.data.enabled
    );
  }

  const campaignObj = state.data;
  delete campaignObj.enabled;

  if (JSON.stringify(campaignObj) !== JSON.stringify(props.campaign)) {
    const { campaign } = await campaignStore.updateCampaign(
      props.campaign.company_id,
      props.campaign.workspace_id,
      state.data
    );

    if (campaign.campaign_id) {
      toast.success("Informations sauvegardées");
    } else {
      toast.error(
        "Une erreur est survenue lors de la modification de votre campagne"
      );
    }
  }

  emits("close");
  props.currentTab = 0;
}

function campaignArchived() {
  close();

  if (route.params.campaignId) router.push({ name: "space" });
}

function changeTab(value) {
  state.currentTab = value; 
  appStore.campaignParametersTab = value;
}

function close() {
  props.currentTab = 0;
  state.cardError = false;
  state.informationsError = false;
  emits("close");
}

watch(
  () => props.isOpen,
  async () => {
    if (
      props.campaign.calibration_id &&
      !campaignStore.campaignsCalibrations.find(
        (elt) => elt.calibration_id === props.campaign.calibration_id
      )
    ) {
      await campaignStore.getCalibration(props.campaign.calibration_id);
    }

    state.data = {
      ...props.campaign,
      enabled: props.campaign.invitation_id ? true : false,
    };
  }
);
</script>
